import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { config } from '../global/config';
import { appInsights, TRACE_LOG_PREFIX } from './appInsights';

// Trace logs are written to the console and sent to Application Insights, so
// they can also be inspected for devices where the console is not reachable
// (e.g. mobile browsers). Disabled in real Production
// (BENEFIT_APP_ENVIRONMENT=PROD); active locally and on UAT, since
// react-scripts always sets NODE_ENV=production for any deployed build.
export const isTraceLogEnabled = config.environment?.toUpperCase() !== 'PROD';

type TraceUser = {
  email?: string;
  clientId?: number | string;
};

let traceUser: TraceUser = {};

// the oidc user is read from storage so traces logged before the customer
// info is loaded (e.g. during signin) are still attributed to the user
const getOidcSubject = (): string | undefined => {
  try {
    const stored = localStorage.getItem(
      `oidc.user:${config.idpAuthority}:${config.idpClientId}`,
    );
    return stored ? JSON.parse(stored)?.profile?.sub : undefined;
  } catch {
    return undefined;
  }
};

// Attaches the customer to every trace and sets the App Insights authenticated
// user context (user_AuthenticatedId = oidc sub, user_AccountId = email), so
// all telemetry of the session can be filtered by user.
export const setTraceUser = (user: TraceUser) => {
  if (!isTraceLogEnabled) return;
  traceUser = user;

  try {
    const userId = getOidcSubject();
    if (userId) {
      appInsights.setAuthenticatedUserContext(userId, user.email, false);
    }
  } catch {
    // telemetry must never break the app flow
  }
};

type LogTraceOptions = {
  severity?: SeverityLevel;
  // send the buffered telemetry right away; use it before navigating away
  // (e.g. signoutRedirect) so the last traces are not lost
  flush?: boolean;
};

export const logTrace = (
  message: string,
  data?: Record<string, unknown>,
  { severity = SeverityLevel.Information, flush = false }: LogTraceOptions = {},
) => {
  if (!isTraceLogEnabled) return;
  const clientTime = new Date().toISOString();
  // eslint-disable-next-line no-console
  console.log(`${TRACE_LOG_PREFIX} ${clientTime} ${message}`, data ?? '');

  try {
    appInsights.trackTrace(
      { message: `${TRACE_LOG_PREFIX} ${message}`, severityLevel: severity },
      {
        ...data,
        clientTime,
        online: navigator.onLine,
        visibility: document.visibilityState,
        userId: getOidcSubject(),
        email: traceUser.email,
        clientId: traceUser.clientId,
      },
    );
    if (flush) {
      appInsights.flush();
    }
  } catch {
    // telemetry must never break the app flow
  }
};
